import {
  Building,
  FileText,
  HelpCircle,
  Home,
  Info,
  Leaf,
  Search,
  Server,
  ShoppingCart,
  Sun,
  Wallet,
} from "lucide-react";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { actions } from "../actions";
import { RootState } from "../store";

// Simple className utility function
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

// Simple Dialog component
interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

const Dialog: React.FC<DialogProps> = ({ open, onOpenChange, children }) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[20vh]"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onOpenChange(false);
        }
      }}
    >
      <div className="relative w-full max-w-lg mx-4">{children}</div>
    </div>
  );
};

const DialogContent: React.FC<{ children: ReactNode; onClose: () => void }> = ({
  children,
  onClose,
}) => {
  return (
    <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden max-h-[420px] flex flex-col">
      <div className="relative flex-1 overflow-hidden">{children}</div>
    </div>
  );
};

// Command components
interface CommandProps {
  children: ReactNode;
  className?: string;
}

const Command: React.FC<CommandProps> = ({ children, className }) => {
  return (
    <div className={cn("flex flex-col h-full", className)}>{children}</div>
  );
};

const CommandInput: React.FC<{
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
}> = ({ placeholder, value, onValueChange }) => {
  return (
    <div className="flex items-center border-b border-border px-4">
      <Search className="w-4 h-4 text-muted-foreground mr-2" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={(e) => {
          // Prevent Enter from submitting if user is typing
          if (e.key === "Enter" && value.trim() === "") {
            e.preventDefault();
          }
        }}
        className="flex h-10 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
        autoFocus
      />
    </div>
  );
};

const CommandList: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <div className="max-h-[280px] overflow-y-auto p-2">{children}</div>;
};

interface CommandGroupProps {
  heading?: string;
  children: ReactNode;
}

const CommandGroup: React.FC<CommandGroupProps> = ({ heading, children }) => {
  return (
    <div className="py-2">
      {heading && (
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase">
          {heading}
        </div>
      )}
      <div className="space-y-1">{children}</div>
    </div>
  );
};

interface CommandItemProps {
  value: string;
  onSelect: () => void;
  children: ReactNode;
  className?: string;
  isSelected?: boolean;
  isActive?: boolean;
}

const CommandItem: React.FC<CommandItemProps> = ({
  onSelect,
  children,
  className,
  isSelected = false,
  isActive = false,
}) => {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-lg px-3 py-2 text-sm outline-none transition-colors text-foreground",
        isActive
          ? "bg-primary/10 text-primary border-l-2 border-primary"
          : isSelected
          ? "bg-primary/20 text-primary border border-primary/30"
          : "hover:bg-muted/50 focus:bg-muted/50",
        className
      )}
    >
      {children}
    </button>
  );
};

const CommandSeparator: React.FC = () => {
  return <div className="h-px bg-border my-2" />;
};

const CommandDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}> = ({ open, onOpenChange, children }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClose={() => onOpenChange(false)}>
        <Command>{children}</Command>
      </DialogContent>
    </Dialog>
  );
};

// Navigation items
const navigationItems = [
  { name: "Platform", href: "/platform", icon: Home, category: "Navigation" },
  {
    name: "Primary Marketplace",
    href: "/primarymarketplace",
    icon: ShoppingCart,
    category: "Navigation",
  },
  {
    name: "Secondary Marketplace",
    href: "/secondarymarketplace",
    icon: Wallet,
    category: "Navigation",
  },
  { name: "Yield", href: "/rental", icon: Building, category: "Navigation" },
  {
    name: "How to Use",
    href: "/how-to-use",
    icon: HelpCircle,
    category: "Navigation",
  },
  { name: "About Us", href: "/about", icon: Info, category: "Navigation" },
];

const categoryItems = [
  {
    name: "Real Estate",
    href: "/rental",
    icon: Building,
    category: "Categories",
  },
  {
    name: "Carbon Credits",
    href: "/primarymarketplace",
    icon: Leaf,
    category: "Categories",
  },
  {
    name: "Renewables",
    href: "/primarymarketplace",
    icon: Sun,
    category: "Categories",
  },
  {
    name: "Data Centres",
    href: "/primarymarketplace",
    icon: Server,
    category: "Categories",
  },
];

const helpItems = [
  {
    name: "How to Use",
    href: "/how-to-use",
    icon: HelpCircle,
    category: "Help",
  },
  { name: "About VESTN", href: "/about", icon: Info, category: "Help" },
  {
    name: "Terms of Service",
    href: "/terms-and-conditions",
    icon: FileText,
    category: "Help",
  },
  {
    name: "Privacy Policy",
    href: "/privacy",
    icon: FileText,
    category: "Help",
  },
];

interface CommandPaletteProps {
  NFTs?: any[];
  getMarketPlaceNFT?: (params?: any) => void;
}

const CommandPaletteComponent: React.FC<CommandPaletteProps> = ({
  NFTs = [],
  getMarketPlaceNFT,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const history = useHistory();
  const location = history.location;

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (open && getMarketPlaceNFT && NFTs.length === 0) {
      getMarketPlaceNFT({ page: 1 });
    }
  }, [open, getMarketPlaceNFT, NFTs.length]);

  const handleSelect = useCallback(
    (href: string) => {
      setOpen(false);
      setSearch("");
      setSelectedIndex(0);
      history.push(href);
    },
    [history]
  );

  // Filter assets based on search
  const filteredAssets = NFTs.filter((asset) => {
    const searchLower = search.toLowerCase();
    return (
      asset.assetName?.toLowerCase().includes(searchLower) ||
      asset.city?.toLowerCase().includes(searchLower) ||
      asset.countryCode?.toLowerCase().includes(searchLower) ||
      asset.assetType?.name?.toLowerCase().includes(searchLower)
    );
  }).slice(0, 5);

  // Filter navigation items
  const filteredNavigation = navigationItems.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  // Filter category items
  const filteredCategories = categoryItems.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  // Filter help items
  const filteredHelp = helpItems.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  const hasResults =
    filteredAssets.length > 0 ||
    filteredNavigation.length > 0 ||
    filteredCategories.length > 0 ||
    filteredHelp.length > 0;

  // Get all items for keyboard navigation (defined after filtered arrays)
  const getAllItems = useCallback(() => {
    const items: Array<{ href: string; type: string }> = [];

    // Add filtered assets (only if search is active)
    if (search) {
      filteredAssets.forEach((asset) => {
        items.push({
          href: `/primaryPropertyDetails/${asset.id || asset._id}`,
          type: "asset",
        });
      });
    }

    // Add navigation items
    (search ? filteredNavigation : navigationItems).forEach((item) => {
      items.push({ href: item.href, type: "navigation" });
    });

    // Add category items
    (search ? filteredCategories : categoryItems).forEach((item) => {
      items.push({ href: item.href, type: "category" });
    });

    // Add help items
    (search ? filteredHelp : helpItems).forEach((item) => {
      items.push({ href: item.href, type: "help" });
    });

    return items;
  }, [
    filteredAssets,
    filteredNavigation,
    filteredCategories,
    filteredHelp,
    search,
    // navigationItems, categoryItems, and helpItems are constants defined outside the component
    // They don't need to be in the dependency array as they don't change between renders
  ]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const allItems = getAllItems();

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : allItems.length - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (allItems[selectedIndex] && allItems.length > 0) {
          handleSelect(allItems[selectedIndex].href);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        setSearch("");
        setSelectedIndex(0);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, selectedIndex, getAllItems, handleSelect]);

  // Initialize selected index to active item when palette opens, or reset when search changes
  useEffect(() => {
    if (search) {
      // When searching, start at first result
      setSelectedIndex(0);
    } else if (open) {
      // When palette opens without search, find the active navigation item
      const activeIndex = navigationItems.findIndex((item) => {
        if (item.href === "/platform") {
          return location.pathname === "/platform" || location.pathname === "/";
        } else if (item.href === "/primarymarketplace") {
          // Check for both /marketplace (canonical) and /primarymarketplace (legacy)
          return (
            location.pathname === "/marketplace" ||
            location.pathname === "/primarymarketplace" ||
            location.pathname.startsWith("/primarymarketplace")
          );
        } else if (item.href === "/secondarymarketplace") {
          return (
            location.pathname === "/secondarymarketplace" ||
            location.pathname.startsWith("/secondarymarketplace") ||
            location.pathname.startsWith("/secondary-marketplace") ||
            location.pathname === "/secondary"
          );
        } else {
          return (
            location.pathname === item.href ||
            location.pathname.startsWith(item.href)
          );
        }
      });

      // Set selected index to active item if found, otherwise 0
      if (activeIndex >= 0) {
        setSelectedIndex(activeIndex);
      } else {
        setSelectedIndex(0);
      }
    }
  }, [search, open, location.pathname]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search assets, pages, actions..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        {!hasResults && search && (
          <div className="py-6 text-center">
            <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
            <p className="text-sm text-muted-foreground">
              No results found for "{search}"
            </p>
          </div>
        )}

        {/* Assets */}
        {search && filteredAssets.length > 0 && (
          <CommandGroup heading="Assets">
            {filteredAssets.map((asset, index) => {
              const assetType = asset.assetType?.name || "";
              // Assets are always first, so index starts at 0
              const isSelected = index === selectedIndex;
              const assetHref = `/primaryPropertyDetails/${
                asset.id || asset._id
              }`;
              const isActive = location.pathname === assetHref;

              return (
                <CommandItem
                  key={asset.id || asset._id}
                  value={asset.assetName || "Untitled Asset"}
                  onSelect={() => handleSelect(assetHref)}
                  className="flex items-center gap-3 cursor-pointer"
                  isSelected={isSelected}
                  isActive={isActive}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    {assetType.includes("Real Estate") ||
                    assetType.includes("Estate") ? (
                      <Building className="w-4 h-4 text-primary" />
                    ) : assetType.includes("Carbon") ? (
                      <Leaf className="w-4 h-4 text-primary" />
                    ) : assetType.includes("Renewable") ||
                      assetType.includes("Solar") ||
                      assetType.includes("Wind") ? (
                      <Sun className="w-4 h-4 text-primary" />
                    ) : assetType.includes("Data") ? (
                      <Server className="w-4 h-4 text-primary" />
                    ) : (
                      <Building className="w-4 h-4 text-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {asset.assetName || "Untitled Asset"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {asset.city || ""}
                      {asset.countryCode ? `, ${asset.countryCode}` : ""}
                      {asset.annualYield ? ` · ${asset.annualYield}% APY` : ""}
                    </p>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Navigation */}
        {(!search || filteredNavigation.length > 0) && (
          <CommandGroup heading="Navigation">
            {(search ? filteredNavigation : navigationItems).map(
              (item, index) => {
                // Calculate item index: assets come first (only if search), then navigation items
                const itemIndex = (search ? filteredAssets.length : 0) + index;
                const isSelected = itemIndex === selectedIndex;

                // Improved active state logic matching header behavior
                let isActive = false;
                if (item.href === "/platform") {
                  isActive =
                    location.pathname === "/platform" ||
                    location.pathname === "/";
                } else if (item.href === "/primarymarketplace") {
                  // Check for both /marketplace (canonical) and /primarymarketplace (legacy)
                  isActive =
                    location.pathname === "/marketplace" ||
                    location.pathname === "/primarymarketplace" ||
                    location.pathname.startsWith("/primarymarketplace");
                } else if (item.href === "/secondarymarketplace") {
                  isActive =
                    location.pathname === "/secondarymarketplace" ||
                    location.pathname.startsWith("/secondarymarketplace") ||
                    location.pathname.startsWith("/secondary-marketplace") ||
                    location.pathname === "/secondary";
                } else {
                  isActive =
                    location.pathname === item.href ||
                    location.pathname.startsWith(item.href);
                }

                return (
                  <CommandItem
                    key={item.href}
                    value={item.name}
                    onSelect={() => handleSelect(item.href)}
                    className="cursor-pointer"
                    isSelected={isSelected && !isActive}
                    isActive={isActive}
                  >
                    <item.icon className="mr-3 h-4 w-4 text-muted-foreground" />
                    <span>{item.name}</span>
                    {isActive && (
                      <span className="ml-auto text-xs text-primary font-medium">
                        Current
                      </span>
                    )}
                  </CommandItem>
                );
              }
            )}
          </CommandGroup>
        )}

        <CommandSeparator />

        {/* Categories */}
        {(!search || filteredCategories.length > 0) && (
          <CommandGroup heading="Asset Categories">
            {(search ? filteredCategories : categoryItems).map(
              (item, index) => {
                // Calculate item index: assets + navigation + categories
                const itemIndex =
                  (search ? filteredAssets.length : 0) +
                  (search
                    ? filteredNavigation.length
                    : navigationItems.length) +
                  index;
                const isSelected = itemIndex === selectedIndex;
                const isActive = location.pathname === item.href;

                return (
                  <CommandItem
                    key={item.href}
                    value={item.name}
                    onSelect={() => handleSelect(item.href)}
                    className="cursor-pointer"
                    isSelected={isSelected}
                    isActive={isActive}
                  >
                    <item.icon className="mr-3 h-4 w-4 text-muted-foreground" />
                    <span>{item.name}</span>
                  </CommandItem>
                );
              }
            )}
          </CommandGroup>
        )}

        <CommandSeparator />

        {/* Help */}
        {(!search || filteredHelp.length > 0) && (
          <CommandGroup heading="Help & Legal">
            {(search ? filteredHelp : helpItems).map((item) => (
              <CommandItem
                key={item.href}
                value={item.name}
                onSelect={() => handleSelect(item.href)}
                className="cursor-pointer"
              >
                <item.icon className="mr-3 h-4 w-4 text-muted-foreground" />
                <span>{item.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>

      <div className="border-t border-border p-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground px-2">
          <span>
            Press{" "}
            <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-muted-foreground">
              ↵
            </kbd>{" "}
            to select
          </span>
          <span>
            Press{" "}
            <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-muted-foreground">
              esc
            </kbd>{" "}
            to close
          </span>
        </div>
      </div>
    </CommandDialog>
  );
};

// Search trigger button for navbar
export const SearchTrigger: React.FC<{ onClick?: () => void }> = ({
  onClick,
}) => (
  <button
    onClick={onClick}
    className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
  >
    <Search className="w-4 h-4" />
    <span>Search...</span>
    <kbd className="ml-2 px-1.5 py-0.5 rounded bg-background/80 border border-border text-xs font-mono text-muted-foreground">
      ⌘K
    </kbd>
  </button>
);

const mapStateToProps = (state: RootState) => {
  const nfts = (state as any).fetchMarketPlaceNFT;
  return {
    NFTs: Array.isArray(nfts) ? nfts : [],
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<RootState, undefined, AnyAction>
) => ({
  getMarketPlaceNFT: (params?: any) =>
    dispatch(actions.getMarketPlaceNFT(params)),
});

export const CommandPalette = connect(
  mapStateToProps,
  mapDispatchToProps
)(CommandPaletteComponent);
